import React, { FC, memo, ReactElement, useCallback, useState } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ContractInfo, ContractPreviewModal, useGetContractPreviewMutation } from 'entities/Contract';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { useCreateContract } from '../utils/hooks/useCreateContract';
import { Box, useGetBoxRentalPriceString } from 'entities/Box';
import { Warehouse } from 'entities/Warehouse';
import { User } from 'entities/User';
import { getServerDateFormat } from 'shared/utils/helpers/getDateFormat';
import { showNotification } from 'app/providers/NotificationsProvider';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getGlobalSettings } from 'app/appState';

interface CreateContractProps {
  warehouse: Nullable<Warehouse>;
  box: Nullable<Box>;
  user: Nullable<User>;
  contractOptions: ContractInfo;
  action: ReactElement;
  onSuccess?: () => void;
}

export const CreateContract: FC<CreateContractProps> = memo((props) => {
  const { contractOptions, box, warehouse, action, user, onSuccess } = props;

  const { t } = useAppTranslation(['booking', 'common']);

  const [isContractPreviewOpened, setContractPreviewOpened] = useState<boolean>(false);

  const globalSettings = useAppSelector(getGlobalSettings);

  const closeContractPreview = useCallback((): void => {
    setContractPreviewOpened(false);
  }, []);

  const handleSuccessCreateContract = useCallback((): void => {
    onSuccess?.();
    closeContractPreview();
  }, [closeContractPreview, onSuccess]);

  const { handleCreateContract, closeConfirmModal, openConfirmModal, isOpenedConfirm, isLoading } = useCreateContract({
    contractOptions,
    boxId: box?.boxId,
    warehouseId: warehouse?.warehouseId,
    userId: user?.userId,
    onSuccess: handleSuccessCreateContract,
  });

  const [getContractPreview, { data, isLoading: getContractPreviewLoading }] = useGetContractPreviewMutation();

  const rentalCost = useGetBoxRentalPriceString(box, contractOptions.invoiceFrequencyType);

  const openContractPreview = useCallback(async (): Promise<void> => {
    if (globalSettings && user && box && warehouse && rentalCost) {
      try {
        await getContractPreview({
          warehouseName: warehouse.name,
          warehouseAddress: warehouse.address,
          startDate: getServerDateFormat(contractOptions.startDate),
          boxId: box.boxId,
          boxNumber: box.name,
          boxSquare: box.sizeCode.square,
          invoiceFrequencyType: contractOptions.invoiceFrequencyType,
          contractDuration: contractOptions.contractDuration,
          discountPercent: contractOptions.discountPercent,
          clientFirstName: user.firstName,
          clientLastName: user.lastName,
          clientStreet: user.street,
          clientCountry: user.country,
          clientCity: user.city,
          clientHouse: user.house,
          clientApartment: user.apartment,
          clientZip: user.zip,
          language: user.language,
          clientEmail: user.email,
          clientPhone: user.phone,
          rentalCost,
          insuranceAmount: contractOptions.insuranceAmountWithoutVat,
          insuranceVatRateAmount: contractOptions.insuranceVatRateAmount,
          discountAmount: contractOptions.discountAmount,
          totalToPay: contractOptions.totalToPay,
          paymentFrequencyPeriod: contractOptions.payForEntirePeriod ? 'entire period' : 'regular',
          vatRateAmount: contractOptions.vatRateAmount,
          currency: globalSettings.currency,
        }).unwrap();
      } catch (error: CustomAny) {
        showNotification('error', t('Error'), t('Error when getting contract preview'));
        console.log(error);
      } finally {
        setContractPreviewOpened(true);
      }
    }
  }, [
    globalSettings,
    user,
    box,
    warehouse,
    rentalCost,
    getContractPreview,
    contractOptions.startDate,
    contractOptions.invoiceFrequencyType,
    contractOptions.contractDuration,
    contractOptions.discountPercent,
    contractOptions.insuranceAmountWithoutVat,
    contractOptions.insuranceVatRateAmount,
    contractOptions.discountAmount,
    contractOptions.totalToPay,
    contractOptions.payForEntirePeriod,
    contractOptions.vatRateAmount,
    t,
  ]);

  return (
    <>
      {React.cloneElement(action, { onClick: openConfirmModal })}
      <ConfirmModal
        isOpened={isOpenedConfirm}
        title={t('Create contract')}
        description={t('Are you sure you want to create contract?')}
        isLoading={isLoading}
        onOk={handleCreateContract}
        onCancel={closeConfirmModal}
      />
      {/* <ContractPreviewModal
        isOpened={isContractPreviewOpened}
        isLoading={getContractPreviewLoading}
        contractPreview={data}
        onOk={handleCreateContract}
        onCancel={closeContractPreview}
      /> */}
    </>
  );
});
